import i18n from '@/lib/i18n';

const state = {
  isLoadingComments: false,
  reviewsSourceName: 'review'
};

const getters = {
  reviews(state, _getters, rootState) {
    switch (state.reviewsSourceName) {
      case 'review':
        return rootState['review']['rawReviews'];
      case 'productMediaReviewCollage':
        return rootState['productMediaReviewCollage']['reviews'];
      default:
        return null;
    }
  },
  reviewsMutationType(state) {
    switch (state.reviewsSourceName) {
      case 'review':
        return 'review/SET_REVIEWS';
      case 'productMediaReviewCollage':
        return 'productMediaReviewCollage/SET_REVIEWS';
      default:
        return null;
    }
  }
};

const mutations = {
  SET_LOADING_COMMENTS(state, isLoadingComments) {
    state.isLoadingComments = isLoadingComments;
  },
  SET_REVIEWS_SOURCE_NAME(state, reviewsSourceName) {
    state.reviewsSourceName = reviewsSourceName;
  }
};

const actions = {
  fetchComments({ commit, getters, rootState }, { reviewId }) {
    commit('SET_LOADING_COMMENTS', true);
    return rootState.api
      .fetchComments(reviewId)
      .then(({ data }) => {
        const renewedReviews = getters.reviews.map(review => {
          if (review.id !== reviewId) return review;

          return {
            ...review,
            comments: data.comments,
            comments_count: data.comments.length
          };
        });
        commit(
          getters.reviewsMutationType,
          { reviews: renewedReviews },
          { root: true }
        );
      })
      .finally(() => commit('SET_LOADING_COMMENTS', false));
  },
  createComment({ commit, getters, rootState }, { reviewId, message }) {
    commit('SET_LOADING_COMMENTS', true);
    return rootState.api
      .createComment(reviewId, { comment: { message } })
      .then(({ data }) => {
        const renewedReviews = getters.reviews.map(review => {
          const relatedReviewIds = [
            review.id,
            ...(review.parent_reviews
              ? review.parent_reviews.map(({ id }) => id)
              : [])
          ];
          if (!relatedReviewIds.includes(reviewId)) return review;

          const newComments = [...review.comments, data.comment];
          return {
            ...review,
            comments: newComments,
            comments_count: newComments.length
          };
        });
        commit(
          getters.reviewsMutationType,
          { reviews: renewedReviews },
          { root: true }
        );
      })
      .finally(() => commit('SET_LOADING_COMMENTS', false));
  },
  deleteComment({ commit, getters, rootState }, { reviewId, commentId }) {
    commit('SET_LOADING_COMMENTS', true);
    return rootState.api
      .deleteComment(commentId)
      .then(() => {
        const renewedReviews = getters.reviews.map(review => {
          const relatedReviewIds = [
            review.id,
            ...(review.parent_reviews
              ? review.parent_reviews.map(({ id }) => id)
              : [])
          ];
          if (!relatedReviewIds.includes(reviewId)) return review;

          const comments = review.comments.filter(e => e.id !== commentId);
          return {
            ...review,
            comments,
            comments_count: comments.length
          };
        });
        commit(
          getters.reviewsMutationType,
          { reviews: renewedReviews },
          { root: true }
        );
      })
      .catch(() =>
        rootState.window.windowService.alert(
          i18n.t('comment.delete_comment_failure')
        )
      )
      .finally(() => commit('SET_LOADING_COMMENTS', false));
  },
  updateComment(
    { commit, getters, rootState },
    { reviewId, commentId, message }
  ) {
    commit('SET_LOADING_COMMENTS', true);
    return rootState.api
      .updateComment(commentId, { comment: { message } })
      .then(() => {
        const renewedReviews = getters.reviews.map(review => {
          const relatedReviewIds = [
            review.id,
            ...(review.parent_reviews
              ? review.parent_reviews.map(({ id }) => id)
              : [])
          ];
          if (!relatedReviewIds.includes(reviewId)) return review;

          const comments = review.comments.map(comment => {
            return comment.id === commentId ? { ...comment, message } : comment;
          });
          return { ...review, comments };
        });
        commit(
          getters.reviewsMutationType,
          { reviews: renewedReviews },
          { root: true }
        );
      })
      .finally(() => commit('SET_LOADING_COMMENTS', false));
  }
};

export default { namespaced: true, state, getters, mutations, actions };
